import { FunctionComponent, useState } from 'react'
import styled from 'styled-components'
import { styles } from '../../../../styles/themes/style'

import ProfilePhoto from '../../../../components/ProfilePhoto'
import Row from '../../../../components/Row'
import Text from '../../../../components/Text'
import SvgFragment from '../../../../fragments/SvgFragment'
import { useAppHandlers } from '../../../../services/actions/app/useAppHandlers'
import { SidebarTypes } from '../../../../interfaces/store/appStore'
import { useConnectionsHandlers } from '../../../../services/actions/connections/useConnectionsHandlers'
import LoaderCell from '../../../../components/LoaderCell'
import { isLNFirst, isMobileDevice } from '../../../../utils'
import { useAuthStore } from '../../../../stores/auth/useAuthStore'

import HomeHighlightsSyncPrompt from './HomeHighlightsSyncPrompt'
import HomeHighlightsSyncingPrompt from './HomeHighlightsSyncingPrompt'
import HomeHighlightsMobileDesktopPrompt from './HomeHighlightsMobileDesktopPrompt'
import { JOB_CHANGE_PREVIEW_CONNECTIONS } from './homeHighlightsPreviewData'

interface IHomeJobChangesTableProps {
    jobChangeConnections: any;
    jobChangeConnectionsLoading: boolean;
    isSynced?: boolean;
    syncInProgress?: boolean;
    extensionInstalled?: boolean;
    onSyncContacts?: () => void;
}

const HomeJobChangesTable: FunctionComponent<IHomeJobChangesTableProps> = ({
    jobChangeConnections,
    jobChangeConnectionsLoading,
    isSynced = true,
    syncInProgress = false,
    extensionInstalled = true,
    onSyncContacts,
}) => {
    const { openSidebar } = useAppHandlers()
    const { messageClickHandler, visitConnectionAndUpdateHandler } = useConnectionsHandlers()
    
    const { store: { user }} = useAuthStore()

    const [loadingConversation, setLoadingConversation] = useState<string | null>(null)

    const sayCongratsHandler = async (e: React.MouseEvent<HTMLElement>, connection: any) => {
        e.stopPropagation()
        setLoadingConversation(connection?._id)
        
        await messageClickHandler(e, connection, setLoadingConversation, `Congrats on starting your new role as ${connection?.profileChangedAt?.job?.newTitle || connection?.profileChangedAt?.job?.previousTitle} at ${connection.profileChangedAt?.company?.newCompany || connection.profileChangedAt?.company?.previousCompany || connection?.company}! Wishing you lots of success!`)
    }

    const renderJobChangeRow = (connection: any, index: number, total: number, faded = false) => {
        const isPreview = !!connection._isPreview
        const isLinkedinFirst = isPreview || isLNFirst(connection, user?._id);

        const isSameJobTitle = connection?.profileChangedAt?.job?.previousTitle &&
            connection?.profileChangedAt?.job?.previousTitle === connection?.profileChangedAt?.job?.newTitle;

        const isSameCompany = connection?.profileChangedAt?.company?.previousCompany &&
            connection?.profileChangedAt?.company?.previousCompany === connection?.profileChangedAt?.company?.newCompany;

        const isLast = index === total - 1

        return (
            <JobChangeGroup key={connection._id ?? index} $isLast={isLast} $faded={faded}>
                <ListRow gap="20px" alignItems>
                    <AvatarCell>
                        <ProfilePhoto
                            source={connection.profilePicture}
                            width="40px"
                            height="40px"
                            minWidth="40px"
                            minHeight="40px"
                            capitalLetters={`${connection.firstName?.charAt(0) || ''}${connection.lastName?.charAt(0) || ''}`}
                        />
                    </AvatarCell>
                    <JobChangeText>
                        <JobChangeTextInner>
                            <JobChangePersonName
                                type="button"
                                disabled={faded || isPreview}
                                onClick={() =>
                                    !faded && !isPreview && openSidebar(SidebarTypes.CONNECTION_ACTIONS, { connection })
                                }
                            >
                                {`${connection.firstName || ''} ${connection.lastName || ''}`.trim()}
                            </JobChangePersonName>
                            {connection?.profileChangedAt?.job?.previousTitle && !isSameJobTitle ? (
                                <>
                                    <JobChangeInlineText> has changed a position from </JobChangeInlineText>
                                    <JobChangeStrikethroughText>
                                        {connection.profileChangedAt.job.previousTitle}
                                    </JobChangeStrikethroughText>
                                    <JobChangeInlineText> to </JobChangeInlineText>
                                    <JobChangeBoldText>
                                        {connection?.profileChangedAt?.job?.newTitle || connection?.jobTitle}
                                    </JobChangeBoldText>
                                </>
                            ) : (
                                <>
                                    <JobChangeInlineText>
                                        {isSameJobTitle ? ' has a position as ' : ' started a new position as '}
                                    </JobChangeInlineText>
                                    <JobChangeBoldText>
                                        {connection?.profileChangedAt?.job?.newTitle || connection?.jobTitle}
                                    </JobChangeBoldText>
                                </>
                            )}
                            <JobChangeInlineText> at </JobChangeInlineText>
                            {connection?.profileChangedAt?.company?.previousCompany && !isSameCompany ? (
                                <>
                                    <JobChangeStrikethroughText>
                                        {connection.profileChangedAt.company.previousCompany}
                                    </JobChangeStrikethroughText>
                                    <JobChangeInlineText> </JobChangeInlineText>
                                </>
                            ) : null}
                            <JobChangeBoldText>
                                {connection.profileChangedAt?.company?.newCompany ||
                                    connection.profileChangedAt?.company?.previousCompany ||
                                    connection?.company}
                            </JobChangeBoldText>
                            <JobChangeInlineText>.</JobChangeInlineText>
                        </JobChangeTextInner>
                    </JobChangeText>
                    <CtaColumn>
                        {isLinkedinFirst ? (
                            <JobChangeCta
                                type="button"
                                disabled={faded || isPreview || loadingConversation === connection?._id}
                                onClick={(e) => !faded && !isPreview && sayCongratsHandler(e, connection)}
                            >
                                {loadingConversation === connection?._id
                                    ? 'Loading conversation...'
                                    : 'Say congrats'}
                            </JobChangeCta>
                        ) : (
                            <JobChangeCta
                                type="button"
                                disabled={faded || isPreview}
                                onClick={() => !faded && !isPreview && visitConnectionAndUpdateHandler(connection)}
                            >
                                Connect
                            </JobChangeCta>
                        )}
                    </CtaColumn>
                </ListRow>
            </JobChangeGroup>
        )
    }

    const previewConnections = !isSynced ? JOB_CHANGE_PREVIEW_CONNECTIONS : jobChangeConnections
    const isMobile = isMobileDevice()

    if (jobChangeConnectionsLoading) {
        return (
            <div style={{ width: '100%' }}>
                {Array(4).fill(undefined).map((_, rowIndex: number) => (
                    <PreloaderRow key={rowIndex}>
                        <LoaderCell $width="100%" $height="60px" />
                    </PreloaderRow>
                ))}
            </div>
        )
    }

    if (!isSynced && isMobile) {
        return <HomeHighlightsMobileDesktopPrompt />
    }

    if (!isSynced && syncInProgress) {
        return (
            <List>
                {previewConnections.map((connection: any, index: number) =>
                    renderJobChangeRow(connection, index, previewConnections.length, index > 0)
                )}
                <HomeHighlightsSyncingPrompt />
            </List>
        )
    }

    if (!isSynced) {
        return (
            <List>
                {previewConnections.map((connection: any, index: number) =>
                    renderJobChangeRow(connection, index, previewConnections.length, index > 0)
                )}
                <HomeHighlightsSyncPrompt
                    title="Sync your contacts to discover job changes"
                    subtitle="Find timely reasons to start warm conversations."
                    extensionInstalled={extensionInstalled}
                    onSyncContacts={onSyncContacts ?? (() => {})}
                />
            </List>
        )
    }

    if (jobChangeConnections.length === 0) {
        return (
            <EmptyPageWrapper>
                <SvgFragment type='templateEmptyBlue' />
                <Text $heading4>No job changes to show</Text>
            </EmptyPageWrapper>
        )
    }

    return (
        <List>
            {jobChangeConnections.map((connection: any, index: number) =>
                renderJobChangeRow(connection, index, jobChangeConnections.length)
            )}
        </List>
    )
}

const divider = `1px solid ${styles.colors.black200}`

const List = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
`

interface IJobChangeGroupProps {
    $isLast?: boolean
    $faded?: boolean
}

const JobChangeGroup = styled.div<IJobChangeGroupProps>`
    width: 100%;
    border-bottom: ${({ $isLast }) => ($isLast ? 'none' : divider)};
    ${({ $faded }) => $faded && `
        opacity: 0.35;
        pointer-events: none;
        user-select: none;
    `}
`

const ListRow = styled(Row)`
    width: 100%;
    padding: 12px 0;
    align-items: center;
`

const AvatarCell = styled.div`
    flex: none;
    width: 40px;
    height: 40px;
`

const JobChangeText = styled.div`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    font-family: NunitoSans, 'Nunito Sans', sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: ${styles.colors.black600};
    word-break: break-word;
`

const JobChangeTextInner = styled.span`
    display: inline;
`

const jobChangeTextBase = `
    display: inline;
    gap: 0;
    font-family: NunitoSans, 'Nunito Sans', sans-serif;
    font-size: 14px;
    line-height: 20px;
    vertical-align: baseline;
`

const JobChangeInlineText = styled(Text)`
    ${jobChangeTextBase}
    font-weight: 400;
    color: ${styles.colors.black600};
`

const JobChangeBoldText = styled(Text)`
    ${jobChangeTextBase}
    font-weight: 700;
    color: ${styles.colors.black600};
`

const JobChangeStrikethroughText = styled(Text)`
    ${jobChangeTextBase}
    font-weight: 400;
    color: ${styles.colors.black400};
    text-decoration: line-through;
    text-decoration-color: rgba(0, 0, 0, 0.3);
`

const JobChangePersonName = styled.button`
    ${jobChangeTextBase}
    font-weight: 700;
    color: ${styles.colors.black600};
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover:not(:disabled) {
        color: ${styles.colors.primary600};
    }

    &:disabled {
        cursor: default;
    }
`

const CtaColumn = styled.div`
    display: flex;
    flex-shrink: 0;
    margin-left: auto;
`

const JobChangeCta = styled.button`
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    padding: 4px 8px;
    min-height: 28px;
    height: 28px;
    border-radius: 8px;
    border: 1px solid ${styles.colors.black200};
    background: ${styles.colors.white};
    color: ${styles.colors.black500};
    font-family: NunitoSans, 'Nunito Sans', sans-serif;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover:not(:disabled) {
        background: ${styles.colors.black100};
    }

    &:disabled {
        opacity: 0.55;
        cursor: default;
    }
`

const PreloaderRow = styled(Row)`
    background: ${({ theme: { secondaryColor } }) => secondaryColor};
    align-items: center;
    margin-bottom: 5px;
    border-radius: 4px;
`

const EmptyPageWrapper = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin-top: -45px;

    & svg {
        width: 42px;
        height: 42px;
    }
`

export default HomeJobChangesTable
